import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  PlayIcon,
  StopIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  MapPinIcon,
  UserIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import {
  getSessions,
  createSession,
  createAndStartSession,
  updateSession,
  deleteSession,
  startSession,
  endSession,
  getSessionStats,
  getTeacherSessions,
  getStudentLessons,
  type Session,
  type CreateSessionRequest,
  type UpdateSessionRequest,
  type SessionStats
} from '@/api/sessionsApi'
import { getLessons, type Lesson, type GetLessonsParams } from '@/api/lessonsApi'
import { getUsers, type User } from '@/api/usersApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

// Form schema for creating/editing sessions
const sessionSchema = z.object({
  lessonId: z.string().min(1, 'Lesson is required'),
  teacherId: z.string().min(1, 'Teacher is required'),
  streamIds: z.array(z.string()).min(1, 'At least one stream must be selected'),
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  scheduledStartTime: z.string().min(1, 'Start time is required'),
  scheduledEndTime: z.string().min(1, 'End time is required'),
  location: z.string().optional(),
  institutionId: z.string().optional(), // For super admin institution selection
}).refine(data => {
  const startTime = new Date(data.scheduledStartTime)
  const endTime = new Date(data.scheduledEndTime)
  return endTime > startTime
}, {
  message: "End time must be after start time",
  path: ["scheduledEndTime"]
})

type SessionFormData = z.infer<typeof sessionSchema>

const SessionsPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('all')
  const [teacherFilter, setTeacherFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showEndModal, setShowEndModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState<Session | null>(null)
  const [deleteSession_state, setDeleteSession_state] = useState<Session | null>(null)
  const [sessionNotes, setSessionNotes] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')
  const [actionLoading, setActionLoading] = useState<string | null>(null) // Track which session is being acted upon
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalSessions, setTotalSessions] = useState(0)
  const [pageSize] = useState(12)

  // Available data for form dropdowns
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [teachers, setTeachers] = useState<User[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [loadingLessons, setLoadingLessons] = useState(false)
  const [stats, setStats] = useState<SessionStats>({
    totalSessions: 0,
    upcomingSessions: 0,
    todaysSessions: 0,
    scheduled: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    missed: 0
  })

  const sessionForm = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      lessonId: '',
      teacherId: user?.role !== 'SUPER_ADMIN' && user?.role !== 'SCHOOL_ADMIN' ? user?.id || '' : '',
      streamIds: [],
      title: '',
      description: '',
      scheduledStartTime: '',
      scheduledEndTime: '',
      location: '',
      institutionId: '',
    }
  })

  useEffect(() => {
    fetchSessions()
    fetchLessons()
    fetchTeachers()
    fetchStats()
    if (user?.role === 'SUPER_ADMIN') {
      fetchInstitutions()
    }
  }, [currentPage, searchQuery, statusFilter, dateFilter, teacherFilter, institutionFilter])

  const fetchSessions = async () => {
    setLoading(true)
    try {
      let response;

      // Use role-based APIs for optimized data fetching
      if (user?.role === 'TEACHER') {
        // Teachers see only their assigned sessions
        const teacherParams = {
          page: currentPage,
          limit: pageSize,
          status: statusFilter === 'all' ? undefined : statusFilter,
          dateFrom: dateFilter === 'today' ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString() :
                   dateFilter === 'week' ? (() => {
                     const startOfWeek = new Date()
                     startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
                     startOfWeek.setHours(0, 0, 0, 0)
                     return startOfWeek.toISOString()
                   })() : undefined,
          dateTo: dateFilter === 'today' ? new Date(new Date().setHours(23, 59, 59, 999)).toISOString() : undefined,
          includeAttendance: false // We'll load attendance on demand
        }
        response = await getTeacherSessions(teacherParams)
      } else if (user?.role === 'STUDENT') {
        // Students see lessons they should attend
        const studentParams = {
          page: currentPage,
          limit: pageSize,
          status: statusFilter === 'all' ? undefined : statusFilter,
          dateFrom: dateFilter === 'today' ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString() :
                   dateFilter === 'upcoming' ? new Date().toISOString() : undefined,
          dateTo: dateFilter === 'today' ? new Date(new Date().setHours(23, 59, 59, 999)).toISOString() : undefined
        }
        response = await getStudentLessons(studentParams)
      } else {
        // Admin users see all sessions with original API
        const params: any = {
          page: currentPage,
          limit: pageSize,
          search: searchQuery.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          teacherId: teacherFilter === 'all' ? undefined : teacherFilter,
          institutionId: institutionFilter === 'all' ? undefined : institutionFilter
        }

        // Add date filters for admin
        if (dateFilter === 'today') {
          params.startDate = new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
          params.endDate = new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
        } else if (dateFilter === 'week') {
          const startOfWeek = new Date()
          startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
          startOfWeek.setHours(0, 0, 0, 0)
          params.startDate = startOfWeek.toISOString()
        } else if (dateFilter === 'upcoming') {
          params.startDate = new Date().toISOString()
        }

        response = await getSessions(params)
      }

      setSessions(response.data.sessions || [])
      setTotalPages(response.data.pagination?.totalPages || 1)
      setTotalSessions(response.data.pagination?.totalSessions || response.data.pagination?.totalSessions || 0)
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
      toast.error('Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const fetchLessons = async () => {
    setLoadingLessons(true)
    try {
      // Add institution filter for super admin
      const lessonParams: GetLessonsParams = {}
      if (user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all') {
        lessonParams.institutionId = institutionFilter
      }

      const response = await getLessons(lessonParams)
      const lessonsData = response.data || []
      setLessons(Array.isArray(lessonsData) ? lessonsData : [])
    } catch (error) {
      console.error('Failed to fetch lessons:', error)
      toast.error('Failed to load lessons')
    } finally {
      setLoadingLessons(false)
    }
  }

  const fetchTeachers = async () => {
    try {
      // For teachers, just use themselves as the only option
      if (userRole === 'teacher' && user) {
        // Create a user object from the current user for the dropdown
        const currentUser = user as any
        const teacherUser: User = {
          _id: currentUser._id || currentUser.id || '',
          id: currentUser.id || currentUser._id || '',
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          fullName: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          email: currentUser.email || '',
          phone: '',
          role: currentUser.role || 'TEACHER',
          emailVerified: true,
          profileImage: '',
          institution: currentUser.institution || '',
          status: 'Active',
          createdBy: '',
          createdAt: ''
        }
        setTeachers([teacherUser])
        return
      }

      // Add institution filter for super admin
      const userParams: any = {}
      if (user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all') {
        userParams.institutionId = institutionFilter
      }

      const response = await getUsers(userParams)
      const usersData = response.users || []
      const teachersData = usersData.filter((user: User) =>
        user.role?.toLowerCase().includes('teacher')
      )
      setTeachers(teachersData)
    } catch (error) {
      console.error('Failed to fetch teachers:', error)
      toast.error('Failed to load teachers')
    }
  }

  const fetchStats = async () => {
    try {
      // Add institution filter for super admin
      const statsInstitutionId = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all'
        ? institutionFilter
        : undefined

      const response = await getSessionStats(statsInstitutionId)
      setStats(response.data || {
        totalSessions: 0,
        upcomingSessions: 0,
        todaysSessions: 0,
        scheduled: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        missed: 0
      })
    } catch (error) {
      console.error('Failed to fetch session stats:', error)
      toast.error('Failed to load session statistics')
    }
  }

  const fetchInstitutions = async () => {
    try {
      const response = await institutionsApi.getInstitutions()
      const institutionsData = response || []
      setInstitutions(Array.isArray(institutionsData) ? institutionsData : [])
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
    }
  }

  const handleCreateSession = async (data: SessionFormData) => {
    try {
      const sessionData: CreateSessionRequest = {
        lessonId: data.lessonId,
        teacherId: data.teacherId,
        streamIds: data.streamIds,
        title: data.title,
        description: data.description,
        location: data.location,
        institutionId: data.institutionId,
        scheduledStartTime: new Date(data.scheduledStartTime).toISOString(),
        scheduledEndTime: new Date(data.scheduledEndTime).toISOString(),
      }

      // Check if user wants to start immediately
      const createAction = (document.querySelector('input[name="createAction"]:checked') as HTMLInputElement)?.value

      if (createAction === 'start') {
        // Use create-and-start endpoint
        await createAndStartSession(sessionData)
        toast.success('Session created and started successfully!')
      } else {
        // Use regular create endpoint (scheduled status)
        await createSession(sessionData)
        toast.success('Session scheduled successfully!')
      }

      setShowAddModal(false)
      sessionForm.reset()
      fetchSessions()
      fetchStats()
    } catch (error: any) {
      console.error('Failed to create session:', error)
      const message = error?.response?.data?.message || 'Failed to create session'
      toast.error(message)
    }
  }

  const handleUpdateSession = async (data: SessionFormData) => {
    if (!selectedSession) return

    try {
      const updateData: UpdateSessionRequest = {
        ...data,
        scheduledStartTime: new Date(data.scheduledStartTime).toISOString(),
        scheduledEndTime: new Date(data.scheduledEndTime).toISOString(),
      }

      await updateSession(selectedSession.id || selectedSession._id, updateData)
      toast.success('Session updated successfully!')
      setShowEditModal(false)
      setSelectedSession(null)
      sessionForm.reset()
      fetchSessions()
    } catch (error: any) {
      console.error('Failed to update session:', error)
      const message = error?.response?.data?.message || 'Failed to update session'
      toast.error(message)
    }
  }

  // Open delete confirmation modal
  const handleDeleteSessionClick = (session: Session) => {
    setDeleteSession_state(session)
    setShowDeleteModal(true)
  }

  // Confirm delete session
  const handleConfirmDeleteSession = async () => {
    if (!deleteSession_state) return

    setDeleteLoading(true)
    try {
      await deleteSession(deleteSession_state.id || deleteSession_state._id)
      toast.success('Session deleted successfully!')
      setShowDeleteModal(false)
      setDeleteSession_state(null)
      fetchSessions()
      fetchStats()
    } catch (error) {
      toast.error('Failed to delete session')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleStartSession = async (session: Session) => {
    const sessionId = session.id || session._id
    if (!sessionId) {
      toast.error('Session ID is missing')
      return
    }

    setActionLoading(sessionId)
    try {
      await startSession(sessionId)
      toast.success('Session started successfully!')
      fetchSessions()
      fetchStats()
    } catch (error: any) {
      console.error('Failed to start session:', error)
      const message = error?.response?.data?.message || 'Failed to start session'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleEndSessionClick = (session: Session) => {
    setSelectedSession(session)
    setSessionNotes('')
    setShowEndModal(true)
  }

  const handleEndSession = async () => {
    if (!selectedSession) return

    const sessionId = selectedSession.id || selectedSession._id
    setActionLoading(sessionId)
    try {
      const response = await endSession(sessionId, sessionNotes.trim() || undefined)

      // Show attendance statistics if available
      if (response.data?.attendanceStats) {
        const stats = response.data.attendanceStats
        const message = `Session ended! Duration: ${stats.sessionDurationHours}h | Students: ${stats.presentStudents}/${stats.totalStudents} | Total Hours: ${stats.totalPresentHours}h`
        toast.success(message, { duration: 6000 })
      } else {
        toast.success('Session ended successfully!')
      }

      setShowEndModal(false)
      setSelectedSession(null)
      setSessionNotes('')
      fetchSessions()
      fetchStats()
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to end session'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }


  const handleEditSession = (session: Session) => {
    setSelectedSession(session)
    sessionForm.reset({
      lessonId: session?.lessonId,
      teacherId: session?.teacherId,
      streamIds: session?.streamIds,
      title: session?.title,
      description: session?.description || '',
      scheduledStartTime: format(new Date(session?.scheduledStartTime), "yyyy-MM-dd'T'HH:mm"),
      scheduledEndTime: format(new Date(session?.scheduledEndTime), "yyyy-MM-dd'T'HH:mm"),
      location: session?.location || '',
    })
    setShowEditModal(true)
  }

  const getStatusBadge = (status: Session['status']) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
      in_progress: { color: 'bg-green-100 text-green-800', label: 'In Progress' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      missed: { color: 'bg-orange-100 text-orange-800', label: 'Missed' }
    }

    const config = statusConfig[status]
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const canStartSession = (session: Session) => {
    const now = new Date()
    const startTime = new Date(session?.scheduledStartTime)
    const fiveMinutesBefore = new Date(startTime.getTime() - 5 * 60 * 1000)

    return session?.status === 'scheduled' &&
           now >= fiveMinutesBefore &&
           (user?.role === 'TEACHER' ? session?.teacherId === user.id : true)
  }

  const canEndSession = (session: Session) => {
    return session?.status === 'in_progress' &&
           (user?.role === 'TEACHER' ? session?.teacherId === user.id : true)
  }

  const canManageSession = (session: Session) => {
    if (user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN') return true
    if (user?.role === 'TEACHER') return session?.teacherId === user.id
    return false
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <PlayIcon className="h-8 w-8 text-blue-600 mr-3" />
            Teaching Sessions
          </h1>
          <p className="text-gray-600">Manage and conduct teaching sessions</p>
        </div>
        <div className="mt-4 lg:mt-0 flex space-x-3">
          <Button
            onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
variant="secondary"
            size="sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {viewMode === 'grid' ? 'Table View' : 'Grid View'}
          </Button>
          {(user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'TEACHER') && (
            <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <PlusIcon className="h-4 w-4 mr-2" />
              New Session
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSessions || 0}</p>
              <p className="text-xs text-gray-400">All time</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">{stats.upcomingSessions || 0}</p>
              <p className="text-xs text-gray-400">
                {stats.upcomingSessions > 0 ? 'Scheduled future' : 'No upcoming sessions'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <PlayIcon className={`h-8 w-8 ${stats.inProgress > 0 ? 'text-orange-600 animate-pulse' : 'text-gray-400'}`} />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress || 0}</p>
              <p className="text-xs text-gray-400">
                {stats.inProgress > 0 ? 'Currently active' : 'No active sessions'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-gray-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Today's Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todaysSessions || 0}</p>
              <p className="text-xs text-gray-400">All statuses</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="missed">Missed</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="upcoming">Upcoming</option>
          </select>

          {user?.role === 'SUPER_ADMIN' && (
            <select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Institutions</option>
              {institutions.map(institution => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          )}

          {(user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN') && (
            <select
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Teachers</option>
              {teachers.map(teacher => (
                <option key={teacher.id || teacher._id} value={teacher.id || teacher._id}>
                  {teacher.firstName} {teacher.lastName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12">
          <PlayIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new session.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {sessions.map((session) => (
            <motion.div
              key={session.id || session._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-md border hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{session?.title}</h3>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <BookOpenIcon className="h-4 w-4 mr-1" />
                      <button
                        onClick={() => {
                          navigate(`/attendance-detail/${session.id || session._id}`)
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        title="View Session Attendance"
                      >
                        {session?.lesson?.title}
                      </button>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span>{session?.teacher?.firstName} {session?.teacher?.lastName}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      <span>{format(new Date(session?.scheduledStartTime), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                    {session?.location && (
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        <span>{session?.location}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-600 mb-4">
                      <UsersIcon className="h-4 w-4 mr-1" />
                      <span>{session?.streamIds.length} stream(s)</span>
                      {session?.attendanceCount !== undefined && (
                        <span className="ml-2">• {session?.presentCount || 0} present</span>
                      )}
                      {session?.status === 'completed' && session?.sessionDurationHours && (
                        <span className="ml-2">• {session?.sessionDurationHours}h duration</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(session?.status)}
                  </div>
                </div>

                {session?.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{session?.description}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    {canStartSession(session) && (
                      <Button
                        onClick={() => handleStartSession(session)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        disabled={actionLoading === (session.id || session._id)}
                      >
                        {actionLoading === (session.id || session._id) ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                        ) : (
                          <PlayIcon className="h-3 w-3 mr-1" />
                        )}
                        {actionLoading === (session.id || session._id) ? 'Starting...' : 'Start'}
                      </Button>
                    )}
                    {canEndSession(session) && (
                      <Button
                        onClick={() => handleEndSessionClick(session)}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        disabled={actionLoading === (session.id || session._id)}
                      >
                        <StopIcon className="h-3 w-3 mr-1" />
                        End
                      </Button>
                    )}
                  </div>

                  {canManageSession(session) && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditSession(session)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSessionClick(session)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Streams</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session) => (
                  <tr key={session.id || session._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{session?.title}</div>
                        <div className="text-sm text-gray-500">
                          <button
                            onClick={() => {
                             navigate(`/attendance-detail/${session.id || session._id}`)
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            title="View Session Attendance"
                          >
                            {session?.lesson?.title}
                          </button>
                        </div>
                        {session?.location && (
                          <div className="text-xs text-gray-400 flex items-center mt-1">
                            <MapPinIcon className="h-3 w-3 mr-1" />
                            {session?.location}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session?.teacher?.firstName} {session?.teacher?.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{format(new Date(session?.scheduledStartTime), 'MMM dd, HH:mm')}</div>
                      <div>to {format(new Date(session?.scheduledEndTime), 'HH:mm')}</div>
                      {session?.actualStartTime && (
                        <div className="text-xs text-green-600">
                          Started: {format(new Date(session?.actualStartTime), 'HH:mm')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(session?.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <div>{session?.streamIds?.length} stream(s)</div>
                          {session?.attendanceCount !== undefined && (
                            <div className="text-xs text-gray-400">
                              {session?.presentCount || 0} present
                            </div>
                          )}
                        </div>
                        {(user?.role === 'TEACHER' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'SUPER_ADMIN') && (
                          <button
                            onClick={() => {
                              navigate(`/attendance-detail/${session.id || session._id}`)
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs hover:bg-blue-50 px-2 py-1 rounded"
                            title="View Full Attendance Details"
                          >
                            📊 View Full Attendance
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {canStartSession(session) && (
                          <Button
                            onClick={() => handleStartSession(session)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            disabled={actionLoading === (session.id || session._id)}
                          >
                            {actionLoading === (session.id || session._id) ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <PlayIcon className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                        {canEndSession(session) && (
                          <Button
                            onClick={() => handleEndSessionClick(session)}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700"
                            disabled={actionLoading === (session.id || session._id)}
                          >
                            <StopIcon className="h-3 w-3" />
                          </Button>
                        )}
                        {canManageSession(session) && (
                          <>
                            <button
                              onClick={() => handleEditSession(session)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSessionClick(session)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalSessions)}</span> of{' '}
              <span className="font-medium">{totalSessions}</span> results
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
  variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
  variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Session Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <PlayIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {showAddModal ? 'Create New Session' : 'Edit Session'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {showAddModal ? 'Schedule a new teaching session' : 'Update session details'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    setSelectedSession(null)
                    sessionForm.reset()
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form
                onSubmit={sessionForm.handleSubmit(showAddModal ? handleCreateSession : handleUpdateSession)}
                className="p-6 space-y-6"
              >
                {/* Institution Selection (Super Admin Only) */}
                {user?.role === 'SUPER_ADMIN' && showAddModal && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                      Institution Selection
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Institution <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...sessionForm.register('institutionId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select institution for this session</option>
                        {institutions.map(institution => (
                          <option key={institution.id} value={institution.id}>
                            {institution.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Choose which school this session belongs to
                      </p>
                    </div>
                  </div>
                )}

                {/* Basic Session Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpenIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Basic Information
                  </h4>

                  {/* Lesson Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lesson <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...sessionForm.register('lessonId', {
                        onChange: (e) => {
                          // Auto-fill title and description when lesson is selected
                          const selectedLesson = lessons.find(l => (l.id || l._id) === e.target.value)
                          if (selectedLesson) {
                            // Just use lesson title without teacher name
                            sessionForm.setValue('title', selectedLesson.title)
                            sessionForm.setValue('description', selectedLesson.description || '')
                            sessionForm.setValue('streamIds', selectedLesson.streams?.map(s => s.id) || [])
                            // Set the first assigned teacher by default
                            const firstTeacherId = selectedLesson.teachers?.[0]?.id || selectedLesson.teacher?.id || ''
                            sessionForm.setValue('teacherId', firstTeacherId)
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingLessons}
                    >
                      <option value="">
                        {loadingLessons ? 'Loading lessons...' : 'Select a lesson to base this session on'}
                      </option>
                      {!loadingLessons && lessons.map(lesson => (
                        <option key={lesson.id || lesson._id} value={lesson.id || lesson._id}>
                          {lesson.title} ({lesson.duration} mins)
                        </option>
                      ))}
                      {!loadingLessons && lessons.length === 0 && (
                        <option value="" disabled>No lessons available</option>
                      )}
                    </select>
                    {sessionForm.formState.errors.lessonId && (
                      <p className="text-red-500 text-sm mt-1">{sessionForm.formState.errors.lessonId.message}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Selecting a lesson will auto-fill session details
                    </p>
                  </div>

                  {/* Session Title */}
                  <div className="mb-4">
                    <Input
                      {...sessionForm.register('title')}
                      label="Session Title"
                      placeholder="e.g., Mathematics - Algebra Basics"
                      error={sessionForm.formState.errors.title?.message}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...sessionForm.register('description')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Session description, topics to cover, special instructions..."
                    />
                    {sessionForm.formState.errors.description && (
                      <p className="text-red-500 text-sm mt-1">{sessionForm.formState.errors.description.message}</p>
                    )}
                  </div>
                </div>

                {/* Teacher Assignment */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-green-600 mr-2" />
                    Teacher Assignment
                  </h4>

                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned Teacher <span className="text-red-500">*</span>
                  </label>

                  {user?.role === 'TEACHER' ? (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-green-600 mr-2" />
                        <div>
                          <span className="text-sm font-medium text-green-800">
                            {user.firstName} {user.lastName}
                          </span>
                          <p className="text-xs text-green-600">You will conduct this session</p>
                        </div>
                      </div>
                      <input type="hidden" {...sessionForm.register('teacherId')} value={user.id || (user as any)._id} />
                    </div>
                  ) : (
                    (() => {
                      // Get the selected lesson's teachers
                      const selectedLessonId = sessionForm.watch('lessonId')
                      const selectedLesson = lessons.find(l => (l.id || l._id) === selectedLessonId)
                      const assignedTeachers = selectedLesson?.teachers || (selectedLesson?.teacher ? [selectedLesson.teacher] : [])

                      if (!selectedLessonId) {
                        return (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-500 text-center">Please select a lesson first to see assigned teachers</p>
                          </div>
                        )
                      }

                      if (assignedTeachers.length === 0) {
                        return (
                          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-700 text-center">No teachers assigned to this lesson</p>
                          </div>
                        )
                      }

                      return (
                        <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                          <div className="space-y-2">
                            {assignedTeachers.map((teacher: any) => (
                              <label
                                key={teacher.id || teacher._id}
                                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white transition-colors cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  {...sessionForm.register('teacherId')}
                                  value={teacher.id || teacher._id}
                                  className="text-green-600 focus:ring-green-500"
                                />
                                <div className="flex items-center space-x-2">
                                  <UserIcon className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium text-gray-900">{teacher.name || `${teacher.firstName} ${teacher.lastName}`}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Select the teacher who will conduct this session
                          </p>
                        </div>
                      )
                    })()
                  )}

                  {sessionForm.formState.errors.teacherId && (
                    <p className="text-red-500 text-sm mt-1">{sessionForm.formState.errors.teacherId.message}</p>
                  )}
                </div>

                {/* Class Streams */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UsersIcon className="h-5 w-5 text-purple-600 mr-2" />
                    Class Streams
                  </h4>

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned Streams <span className="text-red-500">*</span>
                  </label>

                  {(() => {
                    // Get the selected lesson's streams
                    const selectedLessonId = sessionForm.watch('lessonId')
                    const selectedLesson = lessons.find(l => (l.id || l._id) === selectedLessonId)
                    const assignedStreams = selectedLesson?.streams || []

                    if (!selectedLessonId) {
                      return (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-500 text-center">Please select a lesson first to see assigned streams</p>
                        </div>
                      )
                    }

                    if (assignedStreams.length === 0) {
                      return (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-700 text-center">No streams assigned to this lesson</p>
                        </div>
                      )
                    }

                    return (
                      <div className="border border-gray-300 rounded-lg p-4 max-h-40 overflow-y-auto bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {assignedStreams.map((stream: any) => (
                            <label key={stream.id} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white transition-colors cursor-pointer">
                              <input
                                type="checkbox"
                                value={stream.id}
                                {...sessionForm.register('streamIds')}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">{stream.name}</span>
                                <p className="text-xs text-gray-500">Grade: {stream.grade || 'Unknown'}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          These are the streams assigned to the selected lesson
                        </p>
                      </div>
                    )
                  })()}

                  {sessionForm.formState.errors.streamIds && (
                    <p className="text-red-500 text-sm mt-1">{sessionForm.formState.errors.streamIds.message}</p>
                  )}

                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <span className="flex items-center">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      {sessionForm.watch('streamIds')?.length || 0} stream(s) selected
                    </span>
                  </div>
                </div>

                {/* Schedule & Location */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <CalendarDaysIcon className="h-5 w-5 text-orange-600 mr-2" />
                    Schedule & Location
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Input
                        {...sessionForm.register('scheduledStartTime')}
                        type="datetime-local"
                        label="Start Time"
                        error={sessionForm.formState.errors.scheduledStartTime?.message}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        {...sessionForm.register('scheduledEndTime')}
                        type="datetime-local"
                        label="End Time"
                        error={sessionForm.formState.errors.scheduledEndTime?.message}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...sessionForm.register('location')}
                      label="Location"
                      placeholder="e.g., Room 101, Science Lab, Main Hall"
                      error={sessionForm.formState.errors.location?.message}
                    />

                    {/* Quick Time Buttons */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quick Schedule</label>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            const now = new Date()
                            const end = new Date(now.getTime() + 45 * 60 * 1000)
                            sessionForm.setValue('scheduledStartTime', now.toISOString().slice(0, 16))
                            sessionForm.setValue('scheduledEndTime', end.toISOString().slice(0, 16))
                          }}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                        >
                          Now (45m)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const now = new Date()
                            const end = new Date(now.getTime() + 90 * 60 * 1000)
                            sessionForm.setValue('scheduledStartTime', now.toISOString().slice(0, 16))
                            sessionForm.setValue('scheduledEndTime', end.toISOString().slice(0, 16))
                          }}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                        >
                          Now (90m)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const tomorrow = new Date()
                            tomorrow.setDate(tomorrow.getDate() + 1)
                            tomorrow.setHours(9, 0, 0, 0) // 9:00 AM tomorrow
                            const end = new Date(tomorrow.getTime() + 60 * 60 * 1000) // 1 hour later
                            sessionForm.setValue('scheduledStartTime', tomorrow.toISOString().slice(0, 16))
                            sessionForm.setValue('scheduledEndTime', end.toISOString().slice(0, 16))
                          }}
                          className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
                        >
                          Tomorrow 9AM
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Duration Preview & Validation */}
                  <div className="mt-3 space-y-2">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-sm">
                        <ClockIcon className="h-4 w-4 text-blue-600 mr-2" />
                        <span className="text-blue-700">
                          Duration: {(() => {
                            const start = sessionForm.watch('scheduledStartTime')
                            const end = sessionForm.watch('scheduledEndTime')
                            if (start && end) {
                              const startDate = new Date(start)
                              const endDate = new Date(end)
                              const diff = endDate.getTime() - startDate.getTime()

                              if (diff <= 0) {
                                return <span className="text-red-600">⚠ End time must be after start time</span>
                              }

                              const hours = Math.floor(diff / (1000 * 60 * 60))
                              const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
                              return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
                            }
                            return 'Select start and end time'
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Conflict Warning */}
                    {(() => {
                      const start = sessionForm.watch('scheduledStartTime')
                      const end = sessionForm.watch('scheduledEndTime')
                      const teacherId = sessionForm.watch('teacherId')

                      if (start && end && teacherId && new Date(end) > new Date(start)) {
                        // Check for potential conflicts (simplified check)
                        const startTime = new Date(start)
                        const now = new Date()

                        if (startTime < now && startTime.toDateString() === now.toDateString()) {
                          return (
                            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-xs text-yellow-700">
                                ⚠ Scheduled time is in the past. Session will start immediately if "Start immediately" is selected.
                              </p>
                            </div>
                          )
                        }
                      }
                      return null
                    })()}
                  </div>
                </div>

                {/* Session Preview */}
                {showAddModal && (
                  <div className="border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      Session Preview
                    </h4>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Title:</span>
                        <span className="font-medium text-gray-900">{sessionForm.watch('title') || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Teacher:</span>
                        <span className="font-medium text-gray-900">
                          {(() => {
                            const teacherId = sessionForm.watch('teacherId')
                            const teacher = teachers.find(t => (t.id || t._id) === teacherId)
                            return teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Not selected'
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Streams:</span>
                        <span className="font-medium text-gray-900">
                          {sessionForm.watch('streamIds')?.length || 0} selected
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium text-gray-900">{sessionForm.watch('location') || 'Not set'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Session Options */}
                {showAddModal && (
                  <div className="border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 text-indigo-600 mr-2" />
                      Session Options
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Create and...
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="createAction"
                              value="schedule"
                              defaultChecked
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Schedule for later</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="createAction"
                              value="start"
                              className="text-green-600 focus:ring-green-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Start immediately</span>
                          </label>
                        </div>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start">
                          <CalendarDaysIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                          <div>
                            <p className="text-xs font-medium text-yellow-800">Smart Scheduling</p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Use "Start Now" buttons above for quick scheduling, or set custom times.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-between items-center pt-6 border-t bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                  <div className="text-xs text-gray-500">
                    {showAddModal ? (
                      <span className="flex items-center">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Session will be created with "scheduled" status
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Editing existing session
                      </span>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowAddModal(false)
                        setShowEditModal(false)
                        setSelectedSession(null)
                        sessionForm.reset()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className={(() => {
                        if (!showAddModal) return "bg-blue-600 hover:bg-blue-700"
                        const createAction = document.querySelector('input[name="createAction"]:checked') as HTMLInputElement
                        return createAction?.value === 'start'
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-blue-600 hover:bg-blue-700"
                      })()}
                    >
                      {(() => {
                        if (!showAddModal) {
                          return (
                            <>
                              <PencilIcon className="h-4 w-4 mr-2" />
                              Update Session
                            </>
                          )
                        }

                        const createAction = document.querySelector('input[name="createAction"]:checked') as HTMLInputElement
                        if (createAction?.value === 'start') {
                          return (
                            <>
                              <PlayIcon className="h-4 w-4 mr-2" />
                              Create & Start Session
                            </>
                          )
                        } else {
                          return (
                            <>
                              <CalendarDaysIcon className="h-4 w-4 mr-2" />
                              Schedule Session
                            </>
                          )
                        }
                      })()}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* End Session Modal */}
      <AnimatePresence>
        {showEndModal && selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">End Session</h3>
                <button
                  onClick={() => {
                    setShowEndModal(false)
                    setSelectedSession(null)
                    setSessionNotes('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Session: {selectedSession?.title}</h4>
                  <p className="text-sm text-gray-600">
                    Started: {selectedSession?.actualStartTime ?
                      format(new Date(selectedSession?.actualStartTime), 'HH:mm') :
                      'Not started'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Duration: {selectedSession?.actualStartTime ?
                      Math.round((Date.now() - new Date(selectedSession?.actualStartTime).getTime()) / (1000 * 60)) + ' minutes elapsed' :
                      'Not started'}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Notes (Optional)
                  </label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add notes about the session, key points covered, attendance issues, etc..."
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> Ending this session will mark it as completed and students will be checked out automatically.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => {
                      setShowEndModal(false)
                      setSelectedSession(null)
                      setSessionNotes('')
                    }}
variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEndSession}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={actionLoading === selectedSession?._id}
                  >
                    {actionLoading === selectedSession?._id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Ending...
                      </>
                    ) : (
                      <>
                        <StopIcon className="h-4 w-4 mr-2" />
                        End Session
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Session Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && deleteSession_state && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => !deleteLoading && setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-red-500" />
                    Delete Session
                  </h3>
                  <button
                    onClick={() => !deleteLoading && setShowDeleteModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={deleteLoading}
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Session Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
                      <PlayIcon className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {deleteSession_state.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {deleteSession_state.lesson?.title || 'No lesson'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    Are you sure you want to delete this session?
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    This action cannot be undone. All attendance records for this session will also be deleted.
                  </p>
                </div>

                {/* Session Details */}
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="flex items-center">
                    <span className="w-24 text-gray-500">Teacher:</span>
                    <span className="font-medium">{deleteSession_state.teacher?.firstName} {deleteSession_state.teacher?.lastName}</span>
                  </p>
                  <p className="flex items-center">
                    <span className="w-24 text-gray-500">Scheduled:</span>
                    <span className="font-medium">
                      {format(new Date(deleteSession_state.scheduledStartTime), 'MMM dd, yyyy HH:mm')}
                    </span>
                  </p>
                  <p className="flex items-center">
                    <span className="w-24 text-gray-500">Status:</span>
                    {getStatusBadge(deleteSession_state.status)}
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleConfirmDeleteSession}
                  disabled={deleteLoading}
                  loading={deleteLoading}
                >
                  Delete Session
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SessionsPage
